import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];
const PRODUCTS = [
  { v: "term", label: "Term Life" },
  { v: "whole", label: "Whole Life" },
  { v: "universal", label: "Universal Life" },
  { v: "final", label: "Final Expense" },
  { v: "not_sure", label: "Not sure — help me choose" },
];

// Build next 14 available weekdays
function upcomingDates(days = 14) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  while (out.length < days) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function iso(d) {
  return d.toISOString().slice(0, 10);
}

export default function Scheduling() {
  const dates = useMemo(() => upcomingDates(14), []);
  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", product_interest: "not_sure", notes: "" });
  const [step, setStep] = useState(1); // 1 = date/time, 2 = form, 3 = success
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!selectedDate) return;
    axios
      .get(`${API}/bookings/booked-slots`, { params: { date: iso(selectedDate) } })
      .then((r) => setBookedSlots(r.data.booked || []))
      .catch(() => setBookedSlots([]));
  }, [selectedDate]);

  const submit = async () => {
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        date: iso(selectedDate),
        time_slot: selectedSlot,
      });
      setBooking(data);
      setStep(3);
      toast.success("Consultation booked. Check your inbox.");
    } catch (e) {
      toast.error("Could not book. Try another slot.");
    } finally {
      setLoading(false);
    }
  };

  const monthLabel = selectedDate?.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <section id="schedule" className="bg-ink text-cream relative overflow-hidden grain" data-testid="scheduling-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <div className="text-gold text-[11px] tracking-[0.3em] mb-8 flex items-center gap-4">
            <span className="w-8 h-[1px] bg-gold" />
            CHAPTER 06 · THE CONVERSATION
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight mb-8"
          >
            One quiet hour. <span className="italic text-gold">No pitch.</span>
          </motion.h2>
          <p className="text-cream/70 text-base md:text-lg leading-relaxed max-w-md font-light">
            Book a private consultation with a licensed broker. Choose a time
            that works — we'll send a Google Calendar invite instantly and follow up
            with a confirmation.
          </p>
          <div className="mt-16 space-y-6">
            {[
              ["01", "Pick a date and 45-minute window"],
              ["02", "Tell us who you're protecting"],
              ["03", "Meet virtually — no obligation"],
            ].map(([n, t]) => (
              <div key={n} className="flex items-start gap-6 border-t border-cream/10 pt-4">
                <span className="font-serif italic text-gold text-3xl font-light">{n}</span>
                <span className="text-cream/80 text-sm mt-2">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="scheduling-step-1"
            >
              <div className="flex items-baseline justify-between mb-8">
                <div className="font-serif text-3xl md:text-4xl">{monthLabel}</div>
                <div className="text-[10px] tracking-[0.3em] text-cream/50">SELECT A DATE</div>
              </div>

              {/* Horizontal date rail */}
              <div className="flex gap-3 overflow-x-auto pb-4 mb-10 -mx-1 px-1 [&::-webkit-scrollbar]:hidden">
                {dates.map((d) => {
                  const active = iso(d) === iso(selectedDate);
                  return (
                    <button
                      key={iso(d)}
                      onClick={() => { setSelectedDate(d); setSelectedSlot(""); }}
                      className={`shrink-0 w-20 h-24 flex flex-col items-center justify-center border transition-colors duration-300 ${
                        active ? "bg-gold text-ink border-gold" : "border-cream/20 text-cream hover:border-gold"
                      }`}
                      data-testid={`date-${iso(d)}`}
                    >
                      <span className="text-[10px] tracking-[0.2em] opacity-70">
                        {d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
                      </span>
                      <span className="font-serif text-3xl mt-1">{d.getDate()}</span>
                    </button>
                  );
                })}
              </div>

              <div className="text-[10px] tracking-[0.3em] text-cream/50 mb-4">SELECT A TIME</div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {SLOTS.map((s) => {
                  const booked = bookedSlots.includes(s);
                  const active = selectedSlot === s;
                  return (
                    <button
                      key={s}
                      onClick={() => !booked && setSelectedSlot(s)}
                      disabled={booked}
                      className={`py-4 border text-sm transition-colors duration-300 ${
                        booked
                          ? "border-cream/10 text-cream/30 line-through cursor-not-allowed"
                          : active
                          ? "border-gold bg-gold text-ink"
                          : "border-cream/20 text-cream hover:border-gold"
                      }`}
                      data-testid={`slot-${s.replace(/[: ]/g, "")}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => selectedSlot && setStep(2)}
                disabled={!selectedSlot}
                className="mt-12 w-full md:w-auto md:min-w-[300px] py-5 px-10 bg-cream text-ink text-[12px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="scheduling-continue-btn"
              >
                Continue &nbsp;→
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="scheduling-step-2"
            >
              <button
                onClick={() => setStep(1)}
                className="text-[11px] tracking-[0.3em] text-cream/60 hover:text-gold mb-8"
                data-testid="scheduling-back-btn"
              >
                ← BACK
              </button>
              <div className="mb-10">
                <div className="text-[10px] tracking-[0.3em] text-gold mb-2">YOUR APPOINTMENT</div>
                <div className="font-serif text-3xl md:text-4xl">
                  {selectedDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                  <span className="text-gold"> · </span>{selectedSlot}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">FULL NAME</label>
                  <input className="line-input-dark" placeholder="Your name"
                    value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    data-testid="booking-name" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">EMAIL</label>
                  <input className="line-input-dark" placeholder="you@example.com" type="email"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="booking-email" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">PHONE</label>
                  <input className="line-input-dark" placeholder="+1 (555) 000-0000"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="booking-phone" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">INTERESTED IN</label>
                  <select className="line-input-dark bg-ink" value={form.product_interest}
                    onChange={(e) => setForm({ ...form, product_interest: e.target.value })}
                    data-testid="booking-product">
                    {PRODUCTS.map((p) => (
                      <option key={p.v} value={p.v} className="bg-ink text-cream">{p.label}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] tracking-[0.3em] text-cream/50">NOTES (OPTIONAL)</label>
                  <textarea className="line-input-dark resize-none" rows={3}
                    placeholder="Anything we should know?"
                    value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="booking-notes" />
                </div>
              </div>

              <button
                onClick={submit}
                disabled={loading}
                className="mt-12 w-full md:w-auto md:min-w-[340px] py-5 px-10 bg-gold text-ink text-[12px] tracking-[0.3em] uppercase hover:bg-cream transition-colors duration-500 disabled:opacity-60"
                data-testid="booking-submit-btn"
              >
                {loading ? "Booking…" : "Confirm consultation →"}
              </button>
            </motion.div>
          )}

          {step === 3 && booking && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="border border-cream/20 p-10 md:p-16 bg-surfaceDark/60"
              data-testid="scheduling-success"
            >
              <div className="text-[10px] tracking-[0.3em] text-gold mb-6">CONFIRMED · 01 / 01</div>
              <div className="font-serif text-4xl md:text-5xl font-light leading-tight mb-6">
                See you {booking.date}, <span className="italic text-gold">{booking.time_slot}</span>.
              </div>
              <p className="text-cream/70 mb-10 max-w-md">
                A confirmation has been sent to {booking.email}. Add the appointment
                to your calendar with one click.
              </p>
              <a
                href={booking.google_calendar_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-4 px-8 py-4 bg-cream text-ink text-[12px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500"
                data-testid="add-to-gcal-link"
              >
                Add to Google Calendar
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2 6h8m0 0L6 2m4 4L6 10" stroke="currentColor" strokeWidth="1.4"/></svg>
              </a>
              <button
                onClick={() => { setStep(1); setSelectedSlot(""); setBooking(null); setForm({ full_name: "", email: "", phone: "", product_interest: "not_sure", notes: "" }); }}
                className="ml-6 text-[11px] tracking-[0.3em] text-cream/60 hover:text-gold"
                data-testid="book-another-btn"
              >
                BOOK ANOTHER
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
